import { AxiosInstance } from 'axios';
import {
  OverviewFilters,
  ListUsersParams,
  ListOrdersParams,
  RevenueFilters,
  KycFilters,
  ExportParams,
  UsersResponse,
  OrdersResponse,
  RevenueResponse,
  KycResponse,
  OverviewResponseFor,
} from '@sdk/analytics/types';

/**
 * Low-level service that maps SDK calls to REST endpoints.
 * All methods assume the Axios instance is already configured with
 * the Authorization header (i.e., use auth.protectedAxios).
 */
export class AnalyticsService {
  constructor(private readonly adminAxios: AxiosInstance) {

  }

  getOverview<F extends OverviewFilters>(filters: F): Promise<OverviewResponseFor<F>> {
    // cast to any for params typing flexibility
    return this.request<OverviewResponseFor<F>>('/admin/analytics/overview', filters);
  }

  listUsers(params: ListUsersParams): Promise<UsersResponse> {
    return this.request<UsersResponse>('/admin/analytics/users', params);
  }

  listOrders(params: ListOrdersParams): Promise<OrdersResponse> {
    return this.request<OrdersResponse>('/admin/analytics/orders', params);
  }

  getRevenue(filters: RevenueFilters): Promise<RevenueResponse> {
    return this.request<RevenueResponse>('/admin/analytics/revenue', filters);
  }

  getKyc(filters: KycFilters): Promise<KycResponse> {
    return this.request<KycResponse>('/admin/analytics/kyc', filters);
  }

  /**
   * Request an export. The server may respond with a 202 and `Location` header,
   * or stream the file directly.
   */
  requestExport(params: ExportParams): Promise<Blob | ArrayBuffer | unknown> {
    return this.adminAxios
      .get('/admin/analytics/export', { params, responseType: 'blob' })
      .then(res => res.data);
  }

  // -----------------------------------------
  // Internal helper
  // -----------------------------------------
  private async request<T>(url: string, params?: object): Promise<T> {
    const res = await this.adminAxios.get<T>(url, { params });
    return res.data;
  }
}
